import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useOptionalAiBuilderSessionStore } from '../stores/AiBuilderSessionProvider';
import { isAiBuilderTurnBusy } from '../stores/aiBuilderTurnRequests';
import { type AiBuilderChatState } from '../stores/types';

/**
 * 채팅이 같은 서버 세션을 쓰는 동안 세션의 작업 표시를 'chat' 으로 둔다 — 턴 진행 중이거나 자료 등록 중일 때.
 *
 * 채팅 상태는 모두 Provider 의 updateState 한 곳을 지나므로, 거기서 매번 부르면 턴 시작·종료·
 * 취소·오류·복원 어느 경로든 따로 챙기지 않아도 표시가 맞는다(syncChatActiveOperation).
 * 자료 등록(/source)은 턴 상태 밖에서 끝날 수 있다 — 패널을 닫아 턴을 취소해도 요청은 남는다. 그래서 진행 중인
 * 등록 수를 따로 세어(holdSourceRegistration) 하나라도 남아 있으면 'chat' 을 놓지 않는다. 그동안 생성기는 같은
 * 세션으로 턴을 시작하지 못한다(SESSION_LOCKED 방지).
 */
export const useAiBuilderChatOperationSync = () => {
  const sessionStore = useOptionalAiBuilderSessionStore();
  const sessionStoreRef = useRef(sessionStore);
  sessionStoreRef.current = sessionStore;
  const latestStateRef = useRef<AiBuilderChatState | null>(null);
  const sourceRegistrationCountRef = useRef(0);

  useEffect(() => {
    return () => {
      sessionStoreRef.current?.endOperation('chat');
    };
  }, []);

  const applyChatOperation = useCallback(() => {
    const store = sessionStoreRef.current;
    if (!store) return;

    const state = latestStateRef.current;
    const isTurnBusy = state ? isAiBuilderTurnBusy(state) : false;
    if (isTurnBusy || sourceRegistrationCountRef.current > 0) {
      store.tryBeginOperation('chat');
    } else {
      store.endOperation('chat');
    }
  }, []);

  const syncChatActiveOperation = useCallback(
    (state: AiBuilderChatState) => {
      latestStateRef.current = state;
      applyChatOperation();
    },
    [applyChatOperation],
  );

  /** 채팅의 자료 등록 요청을 끝날 때까지(성공·실패 모두) 'chat' 작업으로 잡아 둔다. */
  const holdSourceRegistration = useCallback(
    <T>(registration: Promise<T>): Promise<T> => {
      sourceRegistrationCountRef.current += 1;
      applyChatOperation();
      return registration.finally(() => {
        sourceRegistrationCountRef.current -= 1;
        applyChatOperation();
      });
    },
    [applyChatOperation],
  );

  return { syncChatActiveOperation, holdSourceRegistration };
};

/** 채팅 Provider 가 소유한 세션 수명주기를 같은 빌드 화면의 다른 AI 도구에 열어 준다. */
export const useRegisterAiBuilderChatSessionPort = ({
  draftInstanceId,
  ensureSession,
  getChatState,
}: {
  draftInstanceId: string;
  /** 채팅 턴과 무관한 공유 세션 확보 — 채팅 턴 취소가 다른 도구의 세션 확보를 끊지 않는다. */
  ensureSession: () => Promise<string | null>;
  getChatState: () => AiBuilderChatState;
}) => {
  const sessionStore = useOptionalAiBuilderSessionStore();

  useLayoutEffect(() => {
    if (!sessionStore) return;

    return sessionStore.registerPort({
      draftInstanceId,
      getSessionId: () => getChatState().sessionId,
      getUsage: () => getChatState().usage,
      ensureSession: () => ensureSession(),
    });
  }, [draftInstanceId, ensureSession, getChatState, sessionStore]);
};
